import { getApiBaseUrl } from '../config/permanent-urls';
import { syncTokenBalance } from '../utils/tokenUpdate';
import { createInsufficientTokensError, handleInsufficientTokensError } from '../utils/insufficientTokensHandler';

const API_BASE_URL = getApiBaseUrl();

export type ActionType = 
  | 'email_response' 
  | 'recruiter_outreach'
  | 'cover_letter'
  | 'follow_up'
  | 'thank_you_note'
  | 'interview_confirmation'
  | 'offer_response'
  | 'message_review' 
  | 'writing_assistance' 
  | 'legal_review' 
  | 'salary_negotiation';

export type QualityTier = 'standard' | 'premium'

export const ACTION_TYPE_LABELS: Record<ActionType, string> = {
  email_response: 'Email Response',
  recruiter_outreach: 'Recruiter Outreach',
  cover_letter: 'Cover Letter',
  follow_up: 'Follow-Up',
  thank_you_note: 'Thank You Note',
  interview_confirmation: 'Interview Confirmation',
  offer_response: 'Offer Response',
  message_review: 'Message Review',
  writing_assistance: 'Writing Assistance',
  legal_review: 'Legal Review',
  salary_negotiation: 'Salary Negotiation',
}

export const ACTION_OPERATION_KEYS: Record<ActionType, Record<QualityTier, string>> = {
  email_response: {
    standard: 'communicationCoachEmailResponse',
    premium: 'communicationCoachEmailResponsePremium',
  },
  recruiter_outreach: {
    standard: 'communicationCoachRecruiterOutreach',
    premium: 'communicationCoachRecruiterOutreachPremium',
  },
  cover_letter: {
    standard: 'communicationCoachCoverLetter',
    premium: 'communicationCoachCoverLetterPremium',
  },
  follow_up: {
    standard: 'communicationCoachFollowUp',
    premium: 'communicationCoachFollowUpPremium',
  },
  thank_you_note: {
    standard: 'communicationCoachThankYouNote',
    premium: 'communicationCoachThankYouNotePremium',
  },
  interview_confirmation: {
    standard: 'communicationCoachInterviewConfirmation',
    premium: 'communicationCoachInterviewConfirmationPremium',
  },
  offer_response: {
    standard: 'communicationCoachOfferResponse',
    premium: 'communicationCoachOfferResponsePremium',
  },
  message_review: {
    standard: 'communicationCoachMessageReview',
    premium: 'communicationCoachMessageReviewPremium',
  },
  writing_assistance: {
    standard: 'communicationCoachWritingAssistance',
    premium: 'communicationCoachWritingAssistancePremium',
  },
  legal_review: {
    standard: 'communicationCoachLegalReview',
    premium: 'communicationCoachLegalReviewPremium',
  },
  salary_negotiation: {
    standard: 'communicationCoachSalaryNegotiation',
    premium: 'communicationCoachSalaryNegotiationPremium',
  },
}

export interface CoachingContext {
  recruiter_name?: string;
  company?: string;
  position?: string;
  communication_channel?: string;
  goal?: string;
  tone_preference?: string;
  must_include?: string[];
  must_avoid?: string[];
  location?: string;
  additional_notes?: string;
}

export interface CoachingSession {
  session_id: string;
  job_application_id?: string;
  action_type: ActionType;
  input_content: string;
  context?: CoachingContext;
  result?: CoachingResult;
  status: 'processing' | 'completed' | 'failed';
  store_communication?: boolean;
  quality_tier?: QualityTier;
  model_used?: string;
  created_at?: string;
  updated_at?: string;
}

export interface CoachingResult {
  error?: string;
  message?: string;
  generated_response?: {
    subject?: string;
    body?: string;
    key_points?: string[];
    call_to_action?: string;
    timing_guidance?: string;
    personalization_hooks?: string[];
    logistics_checklist?: string[];
    recommended_option?: string;
    response_options?: Array<{
      label?: string;
      summary?: string;
      body?: string;
    }>;
    alternate_versions?: Array<{
      label?: string;
      subject?: string;
      body?: string;
    }>;
  };
  cover_letter?: {
    email_subject?: string;
    full_letter?: string;
    key_match_points?: string[];
    application_notes?: string[];
    customization_notes?: string[];
    tone_rationale?: string;
  };
  review_feedback?: {
    tone_analysis?: {
      assessment?: string;
      strengths?: string[];
      concerns?: string[];
    };
    clarity?: {
      assessment?: string;
      suggestions?: string[];
    };
    grammar_spelling?: {
      errors?: string[];
      corrections?: string[];
    };
    professionalism?: {
      assessment?: string;
      recommendations?: string[];
    };
    risk_flags?: string[];
    overall_feedback?: string;
    improved_version?: string;
  };
  improved_text?: string;
  suggestions?: string[];
  changes_explained?: string;
  writing_tips?: string[];
  alternate_phrasings?: string[];
  disclaimer?: string;
  red_flags?: Array<{
    issue?: string;
    severity?: 'high' | 'medium' | 'low';
    explanation?: string;
  }>;
  missing_terms?: string[];
  negotiation_points?: Array<{
    point?: string;
    suggestion?: string;
  }>;
  overall_assessment?: string;
  recommendations?: string[];
  should_consult_lawyer?: boolean;
  strategy?: string;
  talking_points?: string[];
  counter_offer_suggestions?: {
    salary_range?: string;
    rationale?: string;
  };
  timing?: string;
  non_salary_benefits?: string[];
  sample_scripts?: string[];
  risk_watchouts?: string[];
  quality_tier?: QualityTier;
  model_used?: string;
  timestamp?: string;
  status?: string;
}

export interface StoredCommunication {
  session_id: string;
  action_type: ActionType;
  job_application_id?: string;
  input_content: string;
  context?: CoachingContext;
  result?: CoachingResult;
  status?: 'processing' | 'completed' | 'failed';
  quality_tier?: QualityTier;
  model_used?: string;
  created_at: string;
  updated_at: string;
}

export interface StoredCommunicationUpdate {
  input_content?: string;
  context?: CoachingContext;
  result?: CoachingResult;
}

class CommunicationCoachService {
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  private getActionTypeEndpoint(actionType: ActionType): string {
    const endpoints: Record<ActionType, string> = {
      email_response: 'email-response',
      recruiter_outreach: 'recruiter-outreach',
      cover_letter: 'cover-letter',
      follow_up: 'follow-up',
      thank_you_note: 'thank-you-note',
      interview_confirmation: 'interview-confirmation',
      offer_response: 'offer-response',
      message_review: 'message-review',
      writing_assistance: 'writing-assistance',
      legal_review: 'legal-review',
      salary_negotiation: 'salary-negotiation',
    };
    return endpoints[actionType];
  }

  async createEmailResponseSession(
    input: string,
    context?: CoachingContext,
    storeCommunication?: boolean,
    jobApplicationId?: string,
    qualityTier: QualityTier = 'standard'
  ): Promise<CoachingSession> {
    return this.createSession('email_response', input, context, storeCommunication, jobApplicationId, qualityTier);
  }

  async createFollowUpSession(
    input: string,
    context?: CoachingContext,
    storeCommunication?: boolean,
    jobApplicationId?: string,
    qualityTier: QualityTier = 'standard'
  ): Promise<CoachingSession> {
    return this.createSession('follow_up', input, context, storeCommunication, jobApplicationId, qualityTier);
  }

  async createRecruiterOutreachSession(
    input: string,
    context?: CoachingContext,
    storeCommunication?: boolean,
    jobApplicationId?: string,
    qualityTier: QualityTier = 'standard'
  ): Promise<CoachingSession> {
    return this.createSession('recruiter_outreach', input, context, storeCommunication, jobApplicationId, qualityTier);
  }

  async createCoverLetterSession(
    input: string,
    context?: CoachingContext,
    storeCommunication?: boolean,
    jobApplicationId?: string,
    qualityTier: QualityTier = 'standard'
  ): Promise<CoachingSession> {
    return this.createSession('cover_letter', input, context, storeCommunication, jobApplicationId, qualityTier);
  }

  async createThankYouNoteSession(
    input: string,
    context?: CoachingContext,
    storeCommunication?: boolean,
    jobApplicationId?: string,
    qualityTier: QualityTier = 'standard'
  ): Promise<CoachingSession> {
    return this.createSession('thank_you_note', input, context, storeCommunication, jobApplicationId, qualityTier);
  }

  async createInterviewConfirmationSession(
    input: string,
    context?: CoachingContext,
    storeCommunication?: boolean,
    jobApplicationId?: string,
    qualityTier: QualityTier = 'standard'
  ): Promise<CoachingSession> {
    return this.createSession('interview_confirmation', input, context, storeCommunication, jobApplicationId, qualityTier);
  }

  async createOfferResponseSession(
    input: string,
    context?: CoachingContext,
    storeCommunication?: boolean,
    jobApplicationId?: string,
    qualityTier: QualityTier = 'standard'
  ): Promise<CoachingSession> {
    return this.createSession('offer_response', input, context, storeCommunication, jobApplicationId, qualityTier);
  }

  async createMessageReviewSession(
    input: string,
    context?: CoachingContext,
    storeCommunication?: boolean,
    jobApplicationId?: string,
    qualityTier: QualityTier = 'standard'
  ): Promise<CoachingSession> {
    return this.createSession('message_review', input, context, storeCommunication, jobApplicationId, qualityTier);
  }

  async createWritingAssistanceSession(
    input: string,
    context?: CoachingContext,
    storeCommunication?: boolean,
    jobApplicationId?: string,
    qualityTier: QualityTier = 'standard'
  ): Promise<CoachingSession> {
    return this.createSession('writing_assistance', input, context, storeCommunication, jobApplicationId, qualityTier);
  }

  async createLegalReviewSession(
    input: string,
    context?: CoachingContext,
    storeCommunication?: boolean,
    jobApplicationId?: string,
    qualityTier: QualityTier = 'standard'
  ): Promise<CoachingSession> {
    return this.createSession('legal_review', input, context, storeCommunication, jobApplicationId, qualityTier);
  }

  async createSalaryNegotiationSession(
    input: string,
    context?: CoachingContext,
    storeCommunication?: boolean,
    jobApplicationId?: string,
    qualityTier: QualityTier = 'standard'
  ): Promise<CoachingSession> {
    return this.createSession('salary_negotiation', input, context, storeCommunication, jobApplicationId, qualityTier);
  }

  private async createSession(
    actionType: ActionType,
    input: string,
    context?: CoachingContext,
    storeCommunication?: boolean,
    jobApplicationId?: string,
    qualityTier: QualityTier = 'standard'
  ): Promise<CoachingSession> {
    try {
      if (!jobApplicationId) {
        throw new Error('Communication Coach requires a job application.')
      }

      const endpoint = this.getActionTypeEndpoint(actionType);

      const response = await fetch(
        `${API_BASE_URL}/communication-coach/${endpoint}`,
        {
          method: 'POST',
          headers: this.getAuthHeaders(),
          body: JSON.stringify({
            input_content: input,
            context,
            store_communication: storeCommunication || false,
            job_application_id: jobApplicationId,
            quality_tier: qualityTier,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const tokenError = createInsufficientTokensError(errorData, response.status);
        if (tokenError) {
          handleInsufficientTokensError(tokenError);
          throw tokenError;
        }
        throw new Error(errorData.error || errorData.message || `Failed to create ${actionType} session: ${response.status}`);
      }

      const data = await response.json();
      const sessionData = data.data || data;
      
      // Sync token balance from backend response (backend is source of truth)
      if (sessionData.tokensRemaining !== undefined && sessionData.tokensUsed !== undefined) {
        syncTokenBalance(sessionData.tokensRemaining, sessionData.tokensUsed);
      }

      return {
        session_id: sessionData.session_id,
        job_application_id: jobApplicationId,
        action_type: actionType,
        input_content: input,
        context,
        status: sessionData.status || 'processing',
        store_communication: storeCommunication,
        quality_tier: sessionData.quality_tier || qualityTier,
        model_used: sessionData.model_used,
        created_at: new Date().toISOString(),
      };
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Network error. Please check your internet connection and try again.');
      }
      throw error;
    }
  }

  async getCoachingSession(sessionId: string, _actionType: ActionType): Promise<CoachingSession | null> {
    try {
      // For now, we'll need to poll the stored communications endpoint
      // In the future, we might add a GET endpoint to each action type Lambda
      const stored = await this.getStoredCommunication(sessionId);
      if (stored) {
        return {
          session_id: stored.session_id,
          job_application_id: stored.job_application_id,
          action_type: stored.action_type as ActionType,
          input_content: stored.input_content,
          context: stored.context,
          result: stored.result,
          status: stored.status || 'completed',
          quality_tier: stored.quality_tier,
          model_used: stored.model_used,
          created_at: stored.created_at,
          updated_at: stored.updated_at,
        };
      }
      return null;
    } catch (error) {
      console.error('Error getting coaching session:', error);
      return null;
    }
  }

  async pollCoachingSession(sessionId: string, actionType: ActionType): Promise<CoachingSession | null> {
    // Poll by checking stored communications
    // If result exists, session is completed
    return this.getCoachingSession(sessionId, actionType);
  }

  async getStoredCommunications(jobApplicationId?: string, actionType?: ActionType): Promise<StoredCommunication[]> {
    try {
      const queryParams = new URLSearchParams();
      if (jobApplicationId) {
        queryParams.append('job_application_id', jobApplicationId);
      }
      if (actionType) {
        queryParams.append('action_type', actionType);
      }

      const response = await fetch(
        `${API_BASE_URL}/communication-coach/stored?${queryParams.toString()}`,
        {
          method: 'GET',
          headers: this.getAuthHeaders(),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const tokenError = createInsufficientTokensError(errorData, response.status);
        if (tokenError) {
          handleInsufficientTokensError(tokenError);
          throw tokenError;
        }
        throw new Error(errorData.error || errorData.message || `Failed to get stored communications: ${response.status}`);
      }

      const data = await response.json();
      const result = data.data || data;
      return result.communications || [];
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Network error. Please check your internet connection and try again.');
      }
      throw error;
    }
  }

  async getStoredCommunication(sessionId: string): Promise<StoredCommunication | null> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/communication-coach/stored/${sessionId}`,
        {
          method: 'GET',
          headers: this.getAuthHeaders(),
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        const errorData = await response.json().catch(() => ({}));
        const tokenError = createInsufficientTokensError(errorData, response.status);
        if (tokenError) {
          handleInsufficientTokensError(tokenError);
          throw tokenError;
        }
        // Handle 400 as not found too (for backward compatibility)
        if (response.status === 400 && errorData.error === 'NOT_FOUND') {
          return null;
        }
        throw new Error(errorData.error || errorData.message || `Failed to get stored communication: ${response.status}`);
      }

      const data = await response.json();
      const result = data.data || data;
      return result || null;
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Network error. Please check your internet connection and try again.');
      }
      throw error;
    }
  }

  async updateStoredCommunication(
    sessionId: string,
    updates: StoredCommunicationUpdate,
  ): Promise<StoredCommunication> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/communication-coach/stored/${sessionId}`,
        {
          method: 'PATCH',
          headers: this.getAuthHeaders(),
          body: JSON.stringify(updates),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error ||
          errorData.message ||
          `Failed to update stored communication: ${response.status}`,
        );
      }

      const data = await response.json();
      return (data.data || data) as StoredCommunication;
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Network error. Please check your internet connection and try again.');
      }
      throw error;
    }
  }

  async deleteStoredCommunication(sessionId: string): Promise<void> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/communication-coach/stored/${sessionId}`,
        {
          method: 'DELETE',
          headers: this.getAuthHeaders(),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error ||
          errorData.message ||
          `Failed to delete stored communication: ${response.status}`,
        );
      }
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Network error. Please check your internet connection and try again.');
      }
      throw error;
    }
  }
}

export const communicationCoachService = new CommunicationCoachService();
